import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { apis } from '@modules/apiClients'
import { UserService } from '@duck/api-definitions'
import { toast } from 'sonner'
import { StandardLoader } from '../StandardLoader'
import { useTar } from '@/modules'
import { Button, Card, CardContent, CardHeader, CardTitle } from '@duck/ui'
import { PasswordInput } from '../PasswordInput'

interface ResetPasswordProps {
  token: string
}

export const ResetPassword = ({ token }: ResetPasswordProps) => {
  const navigate = useNavigate()
  const { t } = useTar()

  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  const handleResetPassword = async () => {
    if (!token) {
      setError(t('auth.resetTokenMissing'))
      return
    }

    if (!newPassword || newPassword.length < 8) {
      setError(t('auth.passwordMinLength'))
      return
    }

    if (newPassword !== confirmPassword) {
      setError(t('auth.passwordsDoNotMatch'))
      return
    }

    try {
      setIsLoading(true)
      setError(null)

      const resetRequest: UserService.ResetPasswordRequest = {
        token,
        newPassword,
        confirmPassword
      }

      await apis.userService.authResetPasswordPost(resetRequest)

      setIsSuccess(true)
      toast.success(t('auth.passwordResetSuccess'))

    } catch (error: any) {
      const errorMessage = error?.response?.data?.error || t('auth.passwordResetFailed')
      setError(errorMessage)
      toast.error(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (isSuccess) {
      const id = setTimeout(() => {
        navigate({ to: '/' })
      }, 3000)
      timeoutRef.current = id

      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current)
        }
      }
    }
  }, [isSuccess, navigate])

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="w-full max-w-md">
          <Card>
            <CardHeader className="text-center">
              <CardTitle>{t('auth.resetPasswordTitle')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-error/10 border border-error/20 rounded-lg p-4">
                <p className="text-sm text-center text-error">
                  {t('auth.resetTokenMissing')}
                </p>
              </div>
              <Button
                onClick={() => navigate({ to: '/' })}
                variant="outline"
                className="w-full"
              >
                {t('common.goHome')}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-[calc(100vw-64px)] sm:w-[30rem]">
        <Card>
          <CardHeader className="text-center">
            <CardTitle>{t('auth.resetPasswordTitle')}</CardTitle>
          </CardHeader>

          <CardContent className="space-y-6">
            {isSuccess ? (
              <div className="space-y-4">
                <div className="bg-success/10 border border-success/20 rounded-lg p-4">
                  <div className="flex items-center mb-3">
                    <svg className="w-5 h-5 text-success mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <h4 className="text-sm font-medium text-success">
                      {t('auth.passwordResetSuccess')}
                    </h4>
                  </div>
                  <p className="text-sm text-foreground mt-1">
                    {t('auth.passwordResetSuccessMessage')}
                  </p>
                </div>
                <Button
                  onClick={() => navigate({ to: '/' })}
                  className="w-full"
                >
                  {t('common.goToLogin')}
                </Button>
              </div>
            ) : (
              <>
                <p className="text-sm text-muted-foreground text-center">
                  {t('auth.resetPasswordDescription')}
                </p>

                {error && (
                  <div className="bg-error/10 border border-error/20 rounded-lg p-4">
                    <p className="text-sm text-error">{error}</p>
                  </div>
                )}

                <div className="space-y-4">
                  <div>
                    <label htmlFor="new-password" className="block text-sm font-medium text-foreground mb-2">
                      {t('auth.newPassword')}
                    </label>
                    <PasswordInput
                      id="new-password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder={t('auth.passwordPlaceholder')}
                      disabled={isLoading}
                      autoComplete="new-password"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !isLoading && newPassword && confirmPassword) {
                          handleResetPassword()
                        }
                      }}
                    />
                  </div>

                  <div>
                    <label htmlFor="confirm-password" className="block text-sm font-medium text-foreground mb-2">
                      {t('auth.confirmPassword')}
                    </label>
                    <PasswordInput
                      id="confirm-password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder={t('auth.confirmPasswordPlaceholder')}
                      disabled={isLoading}
                      autoComplete="new-password"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !isLoading && newPassword && confirmPassword) {
                          handleResetPassword()
                        }
                      }}
                    />
                  </div>
                </div>

                <Button
                  onClick={handleResetPassword}
                  disabled={!newPassword || !confirmPassword || newPassword.length < 8 || isLoading}
                  className="w-full"
                >
                  {isLoading ? (
                    <>
                      <StandardLoader size="sm" inline />
                      <span className="ml-2">{t('common.loading')}</span>
                    </>
                  ) : (
                    t('auth.resetPassword')
                  )}
                </Button>

                <Button
                  onClick={() => navigate({ to: '/' })}
                  variant="outline"
                  className="w-full"
                >
                  {t('common.cancel')}
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
